import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCategory, MdSend, MdStop, MdTrackChanges, MdTune } from "react-icons/md";
import { ActionIcon, Badge, Box, Button, Drawer, Group, LoadingOverlay, MultiSelect, Paper, Popover, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useMsal } from "@azure/msal-react";
import { useLogin, getToken } from "../../authConfig";
import { getFilterOptionsApi } from "../../api";
import { SpeechToText } from "../speech-to-text";
import { SpeechStateChange } from "../speech-to-text/SpeechToText";
import { DomainSelector } from "../domain-selector";
import { useDomain, Domain } from "../../DomainContext";
import classes from "./UserInput.module.css";

export interface UserInputProps {
    onSend: (question: string, selectedAssetTypes: string[], selectedInterestAreas: string[], sstRequestIds: string[]) => void;
    isStreaming: boolean;
    isLoading: boolean;
    onStop: () => void;
    onDomainChange?: (domain: Domain) => void;
}

type AssetTypeMultiSelectGroup = { group: string; items: { value: string; label: string }[] };
type InterestAreaMultiSelectItem = { value: string; label: string };

export const UserInput = ({ onSend, isStreaming, isLoading, onStop, onDomainChange }: UserInputProps) => {
    const { i18n, t } = useTranslation();
    const client = useLogin ? useMsal().instance : undefined;
    const { domain } = useDomain();

    const [question, setQuestion] = useState<string>("");
    const [isComposing, setIsComposing] = useState(false);
    const [isSpeechRecording, setIsSpeechRecording] = useState(false);
    const [sstRequestIds, setSstRequestIds] = useState<string[]>([]);

    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const handleOnChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        setQuestion(ev.currentTarget.value);
    };

    const isQuestionEmpty = question.trim().length === 0;
    const isSubmitDisabled = isQuestionEmpty || isSpeechRecording;

    const submitQuestion = () => {
        if (isSpeechRecording) return;
        const trimmed = question.trim();
        if (trimmed.length === 0) return;
        onSend(trimmed, selectedAssetTypes, selectedInterestAreas, sstRequestIds);
        setQuestion("");
        setSstRequestIds([]);
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            submitQuestion();
        }
    };

    const onHandleClick = () => {
        submitQuestion();
    };

    const onSpeechToText = (state: SpeechStateChange) => {
        if (state.state === "start") {
            setIsSpeechRecording(true);
        } else {
            setIsSpeechRecording(false);
            setQuestion(state.text);
            const sstRequestId = state.sstRequestId;
            if (sstRequestId) {
                setSstRequestIds(current => [...current, sstRequestId]);
            }
        }
    };

    const [assetTypeDropdownOpened, { open: openAssetTypeDropdown, close: closeAssetTypeDropdown }] = useDisclosure(false);
    const [interestAreaDropdownOpened, { open: openInterestAreaDropdown, close: closeInterestAreaDropdown }] = useDisclosure(false);

    const isMobile = useMediaQuery("(max-width: 48em)");
    const [filtersDrawerOpened, { open: openFiltersDrawer, close: closeFiltersDrawerDisclosure }] = useDisclosure(false);

    const [assetTypePopoverOpened, setAssetTypePopoverOpened] = useState(false);
    const [interestAreaPopoverOpened, setInterestAreaPopoverOpened] = useState(false);

    const openAssetTypePopover = () => {
        closeInterestAreaDropdown();
        setInterestAreaPopoverOpened(false);
        setAssetTypePopoverOpened(true);
        openAssetTypeDropdown();
    };

    const openInterestAreaPopover = () => {
        closeAssetTypeDropdown();
        setAssetTypePopoverOpened(false);
        setInterestAreaPopoverOpened(true);
        openInterestAreaDropdown();
    };

    const closeAssetTypeFilter = () => {
        closeAssetTypeDropdown();
        if (!isMobile) setAssetTypePopoverOpened(false);
    };

    const closeInterestAreaFilter = () => {
        closeInterestAreaDropdown();
        if (!isMobile) setInterestAreaPopoverOpened(false);
    };

    const setAssetTypePopover = (opened: boolean) => {
        setAssetTypePopoverOpened(opened);
        if (!opened) closeAssetTypeDropdown();
    };

    const setInterestAreaPopover = (opened: boolean) => {
        setInterestAreaPopoverOpened(opened);
        if (!opened) closeInterestAreaDropdown();
    };

    const closeFiltersDrawer = () => {
        closeAssetTypeDropdown();
        closeInterestAreaDropdown();
        closeFiltersDrawerDisclosure();
    };

    const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([]);
    const [selectedInterestAreas, setSelectedInterestAreas] = useState<string[]>([]);
    const [assetTypeOptions, setAssetTypeOptions] = useState<AssetTypeMultiSelectGroup[]>([]);
    const [interestAreaOptions, setInterestAreaOptions] = useState<InterestAreaMultiSelectItem[]>([]);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const idToken = client ? await getToken(client) : undefined;
                const data = await getFilterOptionsApi(idToken, i18n.language, domain);

                const groupMap = new Map<string, { value: string; label: string }[]>();
                for (const at of data.assetTypes) {
                    const group = at.group ?? "";
                    if (!groupMap.has(group)) groupMap.set(group, []);
                    groupMap.get(group)!.push({ value: at.id, label: at.name });
                }
                setAssetTypeOptions(Array.from(groupMap.entries()).map(([group, items]) => ({ group, items })));
                setInterestAreaOptions(data.interestAreas.map(ia => ({ value: ia.key, label: ia.name })));
                setFilterOptionsLoaded(true);
            } catch {
                setFilterOptionsLoaded(false);
            }
        }
        setFilterOptionsLoaded(false);
        load();
    }, [i18n.language, client, domain]);

    // Human and vet interest areas are disjoint vocabularies, so a selection carried across
    // a domain switch would match nothing. Asset types are shared and therefore kept.
    useEffect(() => {
        setSelectedInterestAreas([]);
    }, [domain]);

    const countBadge = (count: number) =>
        count > 0 ? (
            <Badge size="sm" circle variant="white" c="blue" p={0} styles={{ root: { minWidth: 18, height: 18 } }}>
                {count}
            </Badge>
        ) : undefined;

    const totalFilterCount = selectedAssetTypes.length + selectedInterestAreas.length;

    const clearAllFilters = () => {
        setSelectedAssetTypes([]);
        setSelectedInterestAreas([]);
    };

    const assetTypeMultiSelect = (
        <MultiSelect
            label={isMobile ? t("filters.assetTypes") : undefined}
            placeholder={t("filters.assetTypesPlaceholder")}
            data={assetTypeOptions}
            value={selectedAssetTypes}
            onChange={setSelectedAssetTypes}
            dropdownOpened={assetTypeDropdownOpened}
            onDropdownOpen={openAssetTypeDropdown}
            onDropdownClose={closeAssetTypeFilter}
            comboboxProps={{ position: "top", withinPortal: false, transitionProps: { transition: "pop", duration: 200 } }}
        />
    );

    const interestAreaMultiSelect = (
        <MultiSelect
            label={isMobile ? t("filters.interestAreas") : undefined}
            placeholder={t("filters.interestAreasPlaceholder")}
            data={interestAreaOptions}
            value={selectedInterestAreas}
            onChange={setSelectedInterestAreas}
            dropdownOpened={interestAreaDropdownOpened}
            onDropdownOpen={openInterestAreaDropdown}
            onDropdownClose={closeInterestAreaFilter}
            comboboxProps={{ position: "top", withinPortal: false, transitionProps: { transition: "pop", duration: 200 } }}
        />
    );

    return (
        <Box style={{ position: "relative", width: "100%" }}>
            <Paper className={classes.composer} data-testid="chat-composer" radius="xl" shadow="xs" withBorder>
                <Box className={classes.inputArea}>
                    <LoadingOverlay visible={isSpeechRecording} loaderProps={{ type: "dots" }} />
                    <Textarea
                        classNames={{ input: classes.textarea }}
                        value={question}
                        placeholder="Curious about Arthrex products and procedures? Post your question!"
                        autosize
                        minRows={3}
                        maxRows={8}
                        variant="unstyled"
                        onCompositionStart={handleCompositionStart}
                        onCompositionEnd={handleCompositionEnd}
                        onChange={handleOnChange}
                        onKeyDown={onEnterPress}
                    />
                </Box>

                <Group className={classes.footer} justify="space-between" gap="xs" wrap="nowrap" align="center">
                    <Group className={classes.contextControls} gap="xs" wrap="nowrap" align="center">
                        <Tooltip label={t("filters.domainTooltip")} withArrow multiline w={240}>
                            <div>
                                <DomainSelector onDomainChange={onDomainChange} />
                            </div>
                        </Tooltip>

                        {filterOptionsLoaded && isMobile && (
                            <Tooltip label={t("filters.filtersTooltip")} withArrow multiline w={240}>
                                <Button
                                    classNames={{ section: classes.filterButtonSection }}
                                    variant="light"
                                    color={totalFilterCount > 0 ? "blue" : "gray"}
                                    size="xs"
                                    radius="xl"
                                    leftSection={<MdTune size={15} aria-hidden="true" />}
                                    rightSection={countBadge(totalFilterCount)}
                                    onClick={openFiltersDrawer}
                                >
                                    {t("filters.filters")}
                                </Button>
                            </Tooltip>
                        )}

                        {filterOptionsLoaded && !isMobile && (
                            <>
                                <Popover width={400} position="top-start" withArrow shadow="md" opened={assetTypePopoverOpened} onChange={setAssetTypePopover}>
                                    <Popover.Target>
                                        <Tooltip label={t("filters.assetTypesTooltip")} withArrow multiline w={240}>
                                            <Button
                                                classNames={{ section: classes.filterButtonSection }}
                                                variant="light"
                                                color={selectedAssetTypes.length > 0 ? "blue" : "gray"}
                                                size="xs"
                                                radius="xl"
                                                leftSection={<MdCategory size={15} aria-hidden="true" />}
                                                rightSection={countBadge(selectedAssetTypes.length)}
                                                onClick={openAssetTypePopover}
                                            >
                                                {t("filters.assetTypes")}
                                            </Button>
                                        </Tooltip>
                                    </Popover.Target>
                                    <Popover.Dropdown>{assetTypeMultiSelect}</Popover.Dropdown>
                                </Popover>

                                <Popover
                                    width={400}
                                    position="top-start"
                                    withArrow
                                    shadow="md"
                                    opened={interestAreaPopoverOpened}
                                    onChange={setInterestAreaPopover}
                                >
                                    <Popover.Target>
                                        <Tooltip label={t("filters.interestAreasTooltip")} withArrow multiline w={240}>
                                            <Button
                                                classNames={{ section: classes.filterButtonSection }}
                                                variant="light"
                                                color={selectedInterestAreas.length > 0 ? "blue" : "gray"}
                                                size="xs"
                                                radius="xl"
                                                leftSection={<MdTrackChanges size={15} aria-hidden="true" />}
                                                rightSection={countBadge(selectedInterestAreas.length)}
                                                onClick={openInterestAreaPopover}
                                            >
                                                {t("filters.interestAreas")}
                                            </Button>
                                        </Tooltip>
                                    </Popover.Target>
                                    <Popover.Dropdown>{interestAreaMultiSelect}</Popover.Dropdown>
                                </Popover>
                            </>
                        )}
                    </Group>

                    <Group className={classes.actionControls} gap={4} wrap="nowrap">
                        {!isStreaming && !isLoading ? (
                            <>
                                <SpeechToText onSpeechToText={onSpeechToText} />
                                <Tooltip
                                    label={t("userInput.emptyQuestionTooltip")}
                                    disabled={!isQuestionEmpty}
                                    withArrow
                                    events={{ hover: true, focus: true, touch: true }}
                                >
                                    <span className={classes.sendButtonWrapper}>
                                        <ActionIcon
                                            variant="transparent"
                                            color={isSubmitDisabled ? "gray.4" : "dark.6"}
                                            radius="xl"
                                            onClick={onHandleClick}
                                            aria-label={t("userInput.sendQuestion")}
                                            disabled={isSubmitDisabled}
                                            style={{ backgroundColor: "transparent" }}
                                        >
                                            <MdSend size={24} />
                                        </ActionIcon>
                                    </span>
                                </Tooltip>
                            </>
                        ) : (
                            <ActionIcon variant="transparent" color="dark.6" radius="xl" onClick={onStop} aria-label={t("userInput.stopGenerating")}>
                                <MdStop size={24} />
                            </ActionIcon>
                        )}
                    </Group>
                </Group>
            </Paper>

            <Drawer
                opened={filtersDrawerOpened}
                onClose={closeFiltersDrawer}
                position="bottom"
                size="auto"
                radius="md"
                title={
                    <Group justify="space-between" w="100%">
                        <Text fw={600}>{t("filters.filters")}</Text>
                    </Group>
                }
            >
                <Stack gap="md" pb="md">
                    {assetTypeMultiSelect}
                    {interestAreaMultiSelect}
                    <Group justify="space-between" mt="xs">
                        <Button variant="subtle" color="gray" size="sm" onClick={clearAllFilters} disabled={totalFilterCount === 0}>
                            {t("filters.clearAll")}
                        </Button>
                        <Button color="blue" size="sm" onClick={closeFiltersDrawer}>
                            {t("filters.done")}
                        </Button>
                    </Group>
                </Stack>
            </Drawer>
        </Box>
    );
};
